import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

import colors from 'styles/theme/colors';
import { BodySmall, H1 } from 'styles/typography';
import AISparkleIcon from 'svg/AISparkleIcon';
import StateCircleIcon, { StateCircleState } from 'svg/StateCircleIcon';

type Subtask = {
  title: string;
  annotation: ReactNode;
};

type Stage = {
  title: string;
  subtitle?: ReactNode;
  subtasks?: Subtask[];
};

export const FileAnalysisSubtitle = ({ fileCount }: { fileCount: number }) => (
  <>
    Analyzing your <strong>{fileCount} files</strong> to create a comprehensive summary
  </>
);

type AIJobProgressLoaderProps = {
  title: string;
  subtitle: ReactNode;
  stages: Stage[];
  stage: number;
  onLoadingFinished?: () => void;
};

const AIJobProgressLoader = ({ title, subtitle, stages, stage, onLoadingFinished }: AIJobProgressLoaderProps) => {
  const [internalStage, setInternalStage] = useState(stage);

  useEffect(() => {
    // If the AI platform makes a lot of progress very quickly, let's still show each stage for at least 500ms
    // So the UI experience is still smooth.
    if (internalStage !== stage) {
      const timer = setTimeout(() => {
        setInternalStage((prev) => {
          const direction = stage > prev ? 1 : -1;
          return prev + direction;
        });
      }, 250);

      return () => clearTimeout(timer);
    }
  }, [stage, internalStage]);

  useEffect(() => {
    // Even if you're passing in a stage greater than the number of stages (to indicate completion),
    // wait a moment before calling onLoadingFinished so the user can see the completed state.
    // Also, this allows the component to animate through all the progress states before finishing.
    if (internalStage === stages.length + 1) {
      const timer = setTimeout(() => {
        onLoadingFinished?.();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [internalStage, onLoadingFinished, stages.length]);

  return (
    <Wrapper>
      <Header>
        <Title>
          <AISparkleIcon width={30} height={29} />
          <span>{title}</span>
        </Title>
        <Description>{subtitle}</Description>
      </Header>
      <Card>
        {stages.map((stageData, index) => {
          const getStageState = (stageIndex: number) => {
            if (internalStage === 0) return StateCircleState.DORMANT;
            if (stageIndex === internalStage - 1) return StateCircleState.AWOKEN;
            if (stageIndex < internalStage) return StateCircleState.UNLEASHED;
            return StateCircleState.DORMANT;
          };

          const stageState = getStageState(index);
          const isLastStage = index === stages.length - 1;

          return (
            <div key={index}>
              <HeaderRow>
                <StateCircleIcon state={stageState} />
                <ProcessingText $state={stageState}>{stageData.title}</ProcessingText>
              </HeaderRow>

              {!isLastStage && (
                <DetailsRow>
                  <ConnectorWrapper>
                    <ConnectorSVG>
                      <line
                        x1='10'
                        y1='16'
                        x2='10'
                        y2='100%'
                        stroke={colors.black25}
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeDasharray='1, 4'
                        style={{ transform: 'translateY(-8px)' }}
                      />
                    </ConnectorSVG>
                  </ConnectorWrapper>
                  <DetailsContent $state={stageState}>
                    {stageData.subtitle && <ProgressText>{stageData.subtitle}</ProgressText>}
                    {stageData.subtasks?.map((subtask, subtaskIndex) => (
                      <TaskText key={subtaskIndex}>
                        {subtask.title} <TaskCount>({subtask.annotation})</TaskCount>
                      </TaskText>
                    ))}
                  </DetailsContent>
                </DetailsRow>
              )}
            </div>
          );
        })}
      </Card>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Title = styled(H1)`
  font-weight: 900;
  font-size: 30px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;

  > svg {
    margin-top: 4px;
    margin-right: 8px;
    flex-shrink: 0;
  }
`;

const Description = styled(BodySmall)`
  font-size: 14px;
  font-weight: normal;
`;

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 40px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const ProcessingText = styled.div<{ $state: StateCircleState }>`
  font-size: 14px;
  font-weight: 700;
  color: ${({ $state }) => ($state === StateCircleState.DORMANT ? colors.black50 : colors.aiPurple)};
  transition: color 0.5s ease-in-out;
`;

const DetailsRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 24px;
`;

const ConnectorWrapper = styled.div`
  width: 20px;
  flex-shrink: 0;
  align-self: stretch;
  position: relative;
`;

const ConnectorSVG = styled.svg`
  position: absolute;
  height: 100%;
`;

const DetailsContent = styled.div<{ $state: StateCircleState }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 2px 0 36px 0;
  max-height: ${({ $state }) => ($state === StateCircleState.DORMANT ? '40px' : '200px')};
  transition: max-height 0.5s ease-out;
  overflow: hidden;
`;

const ProgressText = styled.div`
  font-size: 10px;
  color: ${colors.black50};
`;

const TaskText = styled.div`
  font-size: 12px;
  color: ${colors.black};
`;

const TaskCount = styled.span`
  font-size: 10px;
  color: ${colors.black50};
`;

export default AIJobProgressLoader;
